import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeOff } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { useSiteSync, useUnobserveSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

/** Letting a watched site go: nothing happens to the site itself, only the panel stops looking. */
export function UnobserveSiteDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const domain = site.primaryDomain ?? '';
  const siteTag = useSiteSync().data?.tag ?? null;

  const release = useUnobserveSite(site.id, {
    onSuccess: () => {
      toast.success('Сайт убран из мониторинга');
      navigate('/sites', { replace: true });
    },
  });

  function close(): void {
    if (release.isPending) return;
    setTyped('');
    release.reset();
    onClose();
  }

  const matches = typed.trim().toLowerCase() === domain;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (matches) release.mutate(typed.trim());
  }

  return (
    <Dialog
      open={open}
      title="Убрать из мониторинга"
      description={site.displayName}
      onClose={close}
      size="sm"
      footer={
        <>
          <Button onClick={close} disabled={release.isPending}>
            Отмена
          </Button>
          <Button variant="danger-solid" icon={EyeOff} busy={release.isPending} disabled={!matches} type="submit" form="unobserve-site">
            Убрать
          </Button>
        </>
      }
    >
      <form id="unobserve-site" className="stack" onSubmit={submit}>
        <p className="field-hint">Проверки сайта прекратятся, его домены освободятся. Сам сайт не изменится.</p>
        {siteTag ? (
          <p className="field-hint">Домен не будет добавлен повторно по тегу «{siteTag}» в 2d2s.</p>
        ) : null}
        <Field label="Для подтверждения введите домен" htmlFor="unobserve-confirm" hint={domain}>
          <Input id="unobserve-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={domain} autoComplete="off" />
        </Field>
        {release.isError ? (
          <p className="field-error" role="alert">
            {errorText(release.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
